// Componente Alert reutilizable para la aplicación Amarsi Control Panel
// Proporciona notificaciones visuales con diferentes tipos y estilos

import React from 'react';
import { twMerge } from 'tailwind-merge';

/**
 * Componente Alert
 * 
 * Muestra mensajes de alerta con diferentes tipos y estilos
 * Soporta cierre manual y auto-ocultamiento opcional
 * 
 * @param {Object} props - Propiedades del componente
 * @param {string} props.type - Tipo de alerta (success, error, warning, info)
 * @param {string} props.title - Título de la alerta (opcional)
 * @param {string} props.message - Mensaje principal
 * @param {boolean} props.dismissible - Permite cerrar la alerta
 * @param {Function} props.onDismiss - Función al cerrar la alerta
 * @param {string} props.className - Clases CSS adicionales
 * @param {React.ReactNode} props.children - Contenido adicional
 */
const Alert = ({
  type = 'info',
  title,
  message,
  dismissible = false,
  onDismiss,
  className = '',
  children
}) => {
  // Configuración de estilos según el tipo
  const typeStyles = {
    success: {
      container: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-200',
      icon: 'text-green-400 dark:text-green-300',
      button: 'text-green-400 hover:bg-green-100 dark:text-green-300 dark:hover:bg-green-800/30'
    },
    error: {
      container: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200',
      icon: 'text-red-400 dark:text-red-300',
      button: 'text-red-400 hover:bg-red-100 dark:text-red-300 dark:hover:bg-red-800/30'
    },
    warning: {
      container: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-200',
      icon: 'text-yellow-400 dark:text-yellow-300',
      button: 'text-yellow-400 hover:bg-yellow-100 dark:text-yellow-300 dark:hover:bg-yellow-800/30'
    },
    info: {
      container: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200',
      icon: 'text-blue-400 dark:text-blue-300',
      button: 'text-blue-400 hover:bg-blue-100 dark:text-blue-300 dark:hover:bg-blue-800/30'
    }
  };

  // Iconos según el tipo
  const icons = {
    success: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
      </svg>
    ),
    error: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
      </svg>
    ),
    warning: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
      </svg>
    ),
    info: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
      </svg>
    )
  };

  const currentStyle = typeStyles[type];

  return (
    <div className={twMerge(
      'rounded-md border p-4',
      currentStyle.container,
      className
    )}>
      <div className="flex">
        <div className="flex-shrink-0">
          <span className={currentStyle.icon}>
            {icons[type]}
          </span>
        </div>
        
        <div className="ml-3 flex-1">
          {title && (
            <h3 className="text-sm font-medium">
              {title}
            </h3>
          )}
          
          <div className={twMerge(
            'text-sm',
            title && 'mt-2'
          )}>
            {message}
          </div>
          
          {children && (
            <div className="mt-2">
              {children}
            </div>
          )}
        </div>
        
        {dismissible && (
          <div className="ml-auto pl-3">
            <div className="-mx-1.5 -my-1.5">
              <button
                onClick={onDismiss}
                className={twMerge(
                  'inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-800',
                  currentStyle.button
                )}
              >
                <span className="sr-only">Cerrar</span>
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Alert;
